// CELL-BIO9 — terminal renderer. Print queue + typewriter effect.
// Default 30ms/char. Any input (key, click, touch) skips the animation.
// Input is never blocked while text is printing.
// Audio clicks are fired per character via the audioEngine singleton.

import type { LineClass, StoryLine } from "./types";
import { audioEngine } from "./audio";

const DEFAULT_CHAR_MS = 30;
// Play a click every N characters to avoid audio spam at high speeds.
const CLICK_EVERY_N_CHARS = 2;

export class Terminal {
  private root: HTMLElement;
  private queue: StoryLine[] = [];
  private printing = false;
  private skipRequested = false;
  private charMs = DEFAULT_CHAR_MS;
  private idleResolvers: Array<() => void> = [];

  constructor(root: HTMLElement) {
    this.root = root;
    // Any input skips the current typewriter animation.
    const skip = (): void => {
      this.skipRequested = true;
    };
    window.addEventListener("keydown", skip);
    window.addEventListener("pointerdown", skip);
  }

  setSpeed(msPerChar: number): void {
    this.charMs = Math.max(0, msPerChar);
  }

  /** Queue one line for typewriter printing. */
  print(line: StoryLine): void {
    this.queue.push(line);
    void this.pump();
  }

  /** Queue several lines. */
  printLines(lines: StoryLine[]): void {
    for (const line of lines) this.print(line);
  }

  /** Print instantly, no animation (system/meta output). Returns the element. */
  printInstant(text: string, cls: LineClass = "system"): HTMLElement {
    const el = this.makeLineEl(cls);
    el.textContent = text;
    this.scrollToBottom();
    return el;
  }

  /** Resolves when the queue is fully drained. */
  whenIdle(): Promise<void> {
    if (!this.printing && this.queue.length === 0) return Promise.resolve();
    return new Promise((resolve) => {
      this.idleResolvers.push(resolve);
    });
  }

  clear(): void {
    this.queue = [];
    this.root.textContent = "";
  }

  private makeLineEl(cls: LineClass | "muted"): HTMLElement {
    const el = document.createElement("p");
    el.className = `line ${cls}`;
    this.root.appendChild(el);
    return el;
  }

  private async pump(): Promise<void> {
    if (this.printing) return;
    this.printing = true;
    while (this.queue.length > 0) {
      const line = this.queue.shift();
      if (!line) break;
      await this.typeLine(line);
    }
    this.printing = false;
    const resolvers = this.idleResolvers;
    this.idleResolvers = [];
    for (const resolve of resolvers) resolve();
  }

  private async typeLine(line: StoryLine): Promise<void> {
    const el = this.makeLineEl(line.class);
    this.skipRequested = false;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || this.charMs === 0) {
      el.textContent = line.text;
      this.scrollToBottom();
      return;
    }

    for (let i = 0; i < line.text.length; i++) {
      if (this.skipRequested) {
        el.textContent = line.text;
        break;
      }
      el.textContent = line.text.slice(0, i + 1);
      this.scrollToBottom();
      // Fire audio click every N characters.
      if (i % CLICK_EVERY_N_CHARS === 0) {
        audioEngine.click();
      }
      await sleep(this.charMs);
    }
    el.textContent = line.text;
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    this.root.scrollTop = this.root.scrollHeight;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
